import React from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import {Box, Button, Grid, Paper, ThemeProvider, Typography} from '@mui/material';
import myTheme from '../styles/myStyles';
import * as STYLE_CONSTANTS from '../styles/styleConstants';
import BlockedImage from '../static/images/warningImage.png';

/**
 * Catches render errors so one broken page does not blank the whole portal.
 *
 * Without a boundary anywhere above them, React unmounts the entire tree when a
 * render throws - the user gets a white screen with nothing on it, and the only
 * trace is a console message they will never look at. That is exactly how the
 * AG Grid "cannot get grid to draw rows while it is drawing rows" crash on the
 * Partner API page presented: reproducible only on production, invisible from
 * the outside, and indistinguishable from a failed deploy.
 *
 * Not a universal net, and worth knowing where it stops: React boundaries catch
 * throws during render, in lifecycle methods and in constructors. They do not
 * catch throws inside event handlers, or inside async callbacks that resolve
 * outside a React update. Those still reach `window.onerror` and still leave
 * the page half-working - they just no longer take the tree down with them,
 * because React never saw them in the first place.
 *
 * A class component on purpose. `componentDidCatch` has no hook equivalent -
 * error boundaries are the one thing hooks still cannot express.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {error: undefined};
  }

  static getDerivedStateFromError(error) {
    return {error};
  }

  componentDidCatch(error, errorInfo) {
    // Kept for the browser console: a white screen tells whoever is looking
    // nothing, and this is the one place that still knows what happened.
    // eslint-disable-next-line no-console
    console.error('Unhandled render error', error, errorInfo?.componentStack);
    // React swallows errors a boundary catches, so Sentry's global handler
    // never sees them - report explicitly, with the component stack.
    Sentry.captureException(error, {contexts: {react: {componentStack: errorInfo?.componentStack}}});
  }

  componentDidUpdate(prevProps) {
    // A new `resetKey` - the route path, where App uses it - clears the error so
    // navigating away from the broken page works. Without this the boundary
    // keeps rendering the fallback over every subsequent route.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({error: undefined});
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <ThemeProvider theme={myTheme}>
        <Grid container sx={{backgroundColor: STYLE_CONSTANTS.COLORS.backgroundMain}}>
          <Grid item xs={12} sm={10} md={6} lg={4} sx={{margin: '160px auto'}}>
            <Paper sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px',
              borderRadius: '10px',
            }}>
              <img src={BlockedImage} alt={''}/>
              <Typography variant={'body1'} align={'center'} sx={{
                fontSize: '18px',
                fontWeight: 600,
                margin: '16px auto 4px',
              }}>
                This page could not be displayed
              </Typography>
              <Typography variant={'body2'} align={'center'} sx={{
                color: STYLE_CONSTANTS.COLORS.fontMain,
                maxWidth: '380px',
                marginBottom: '20px',
              }}>
                Something went wrong while drawing this page. Anything you had
                already saved is safe. Reloading usually clears it - if it keeps
                happening, tell us what you were doing and we will fix it.
              </Typography>
              <Box sx={{display: 'flex', gap: '12px'}}>
                <Button variant={'contained'} onClick={() => window.location.reload()}>
                  Reload this page
                </Button>
                {/*
                  No onClick reset here on purpose: changing the location
                  changes `resetKey`, and componentDidUpdate clears the error
                  then. Clearing it on click instead would re-render the broken
                  route for a frame before the navigation landed, and it would
                  crash again.
                */}
                <Button variant={'outlined'} component={Link} to={'/'}>
                  Go to home
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </ThemeProvider>
    );
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /** Change this to clear a caught error - App passes the current pathname. */
  resetKey: PropTypes.string,
};

ErrorBoundary.defaultProps = {
  children: null,
  resetKey: undefined,
};

export default ErrorBoundary;
